// ContactFormUnified.jsx
// Usage: <ContactFormUnified mode="create" contacts={contacts} companies={companies} contactTypes={contactTypes} languageTypes={languageTypes} onFetchContacts={...} onCloseForm={...} onHandleEstCont={...} />
//        <ContactFormUnified mode="edit" id={contactId} companies={companies} contactTypes={contactTypes} languageTypes={languageTypes} onFetchContacts={...} />

import React, { useMemo, useEffect } from 'react';
import { Button, Grid, Paper, IconButton, Box, Typography } from 'components/mui';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import {
  CompanySelector,
  ContactInput,
  // ContactTypeSelector,
  FullAddressField,
} from 'containers/pages/contacts/components';
import {
  ImgUploader,
  InputNote,
  SelectLanguages,
} from 'containers/pages/contacts/components';
import { InputPhoneLib, PlaceAutoComplete } from 'components';
import * as TY from 'containers/pages/contacts/constants';
import LoadingBackdrop from 'components/LoadingBackdrop';
import ArchiveConfirmationDialog from 'containers/pages/contacts/components/archiveDialog/ArchiveConfirmationDialog';
import {useContactFormUnified} from "containers/pages/contacts/hooks";


function MissingFieldsHint({ canNotSave, missing }) {
  if (!canNotSave || missing.length === 0) return null;
  return (
    <Typography color="error" variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
      Please fill in all required fields: {missing.join(', ')}.
    </Typography>
  );
}

export default function ContactFormUnified(props) {
  const {
    mode = 'create', // 'create' | 'edit'
    companies,
    // contactTypes,
    languageTypes,
  } = props;

  const { state, handler } = useContactFormUnified({ mode, ...props });

  const contactFullName = `${state.contact[TY.FIRST_NAME]} ${state.contact[TY.LAST_NAME]}`.trim() || 'this contact';

  const missingFields = useMemo(() => {
    const missing = [];
    if (!state.contact[TY.FIRST_NAME]) missing.push('First name');
    if (!state.contact[TY.LAST_NAME]) missing.push('Last name');
    // if (!state.contact[TY.CONTACT_TYPES] || state.contact[TY.CONTACT_TYPES].length === 0) missing.push('Contact type');
    if (!state.contact[TY.COMPANIES] || state.contact[TY.COMPANIES].length === 0) missing.push('Company');
    return missing;
  }, [state.contact]);

  // Adds google maps api script to the html head so we can access address autocomlete -> by Zaid Nissar on Sep 10, 2025
  useEffect(() => {
    if (window.google) return;

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.id = "google-maps";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_API_KEY}&libraries=places&loading=async`;
    
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
    };
    
    document.head.appendChild(script); 
  }, []);

  return (
    <Paper 
      elevation={3} 
      sx={{
         p: 2, /* optional vertical gap between sections */ 
        '& .section + .section': { mt: 2 } 
      }}
    >
      {/* Top: Photo + First/Last Name */}
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={3}>
          <Box display="flex" justifyContent="center" height="100%">
            <ImgUploader contact={state.contact} dispatch={handler.dispatch} />
          </Box>
        </Grid>
        <Grid item xs={12} md={9} display="flex" justifyContent="center">
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6}>
              <ContactInput
                name={TY.FIRST_NAME}
                label="First Name"
                type="text"
                required
                value={state.contact[TY.FIRST_NAME]}
                valid={state.valid[TY.FIRST_NAME]}
                onChange={handler.onForm}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ContactInput
                name={TY.LAST_NAME}
                label="Last Name"
                type="text"
                required
                value={state.contact[TY.LAST_NAME]}
                valid={state.valid[TY.LAST_NAME]}
                onChange={handler.onForm}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box mt={2} />

      {/* Middle: Three columns */}
      <Grid container spacing={3}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={2}>
            <ContactInput
                name={TY.CONTACT_POSITION}
                label="Contact Type"
                type="text"
                value={state.contact[TY.CONTACT_POSITION]}
                valid={state.valid[TY.CONTACT_POSITION]}
                onChange={handler.onForm}
                fullWidth
              />
            <CompanySelector
              value={state.contact[TY.COMPANIES]}
              options={companies}
              handleForm={handler.onForm}
              valid={state.valid[TY.COMPANIES]}
            />
            <ContactInput
              name={TY.EMAIL_WORK}
              label="Work Email"
              type="email"
              value={state.contact[TY.EMAIL_WORK]}
              valid={state.valid[TY.EMAIL_WORK]}
              onChange={handler.onForm}
              fullWidth
            />
            <InputPhoneLib
              name={TY.PHONE_WORK}
              label="Work Phone"
              value={state.contact[TY.PHONE_WORK]}
              onChange={handler.onForm}
              onChangeValid={handler.onValid}
              fullWidth
            />
          </Box>
        </Grid>

        {/* MIDDLE COLUMN */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={2}>
            <ContactInput
              name={TY.EMAIL_PERSONAL}
              label="Personal Email"
              type="email"
              value={state.contact[TY.EMAIL_PERSONAL]}
              valid={state.valid[TY.EMAIL_PERSONAL]}
              onChange={handler.onForm}
              fullWidth
            />
            <InputPhoneLib
              name={TY.PHONE_MOBILE}
              label="Personal Phone"
              value={state.contact[TY.PHONE_MOBILE]}
              onChange={handler.onForm}
              onChangeValid={handler.onValid}
              fullWidth
            />
            {!state.contact[state.addressType]?.fullAddress ? (
              <PlaceAutoComplete handlePlace={handler.onPlace} />
            ) : (
              <FullAddressField
                type={state.addressType}
                value={state.contact[state.addressType].fullAddress}
                contact={state.contact}
                dispatch={handler.dispatch}
              />
            )}
            <ContactInput
              name={TY.UNIT}
              label="Unit #"
              type="text"
              value={state.contact[state.addressType]?.unit || ''}
              onChange={handler.onForm}
              fullWidth
            />
          </Box>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <SelectLanguages value={state.contact[TY.LANGUAGES]} dispatch={handler.dispatch} options={languageTypes} />
          <Grid
            item
            paddingTop={'15px'}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              '& .MuiTextField-root': { flex: 1, minHeight: 0, alignSelf: 'stretch' },
              '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
              '& .MuiInputBase-multiline': { height: '100%' },
              '& textarea': { height: '100%', boxSizing: 'border-box', overflow: 'auto', resize: 'none' },
            }}
          >
            <InputNote contact={state.contact} handleForm={handler.onForm} />
          </Grid>
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box display="flex" gap={2} mt={3} alignItems="flex-start">
        <Box flex="1" />
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <MissingFieldsHint canNotSave={state.canNotSave} missing={missingFields} />
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              disabled={state.canNotSave}
              onClick={handler.onSave}
              title={state.canNotSave && missingFields.length > 0 ? `Please fill in: ${missingFields.join(', ')}` : undefined}
            >
              {mode === 'create' ? 'CREATE' : 'SAVE'}
            </Button>
            <Button variant="outlined" onClick={handler.onReset} name={TY.RESET}>
              RESET
            </Button>
            {mode === 'edit' && (
              <IconButton color="warning" onClick={handler.onArchiveConfirm} disabled={state.isLoading} title="Delete/Archive">
                <DeleteOutlineIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      <LoadingBackdrop open={state.isLoading} />
      {mode === 'edit' && (
        <ArchiveConfirmationDialog
          open={state.showArchiveConfirm}
          onClose={handler.onArchiveCancel}
          onConfirm={handler.onArchive}
          contactName={contactFullName}
          loading={state.isLoading}
        />
      )}
    </Paper>
  );
}